/* eslint-disable @typescript-eslint/no-explicit-any */
// This component selects an employee which can be of role employee or service provider.

import React, { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  TextField,
  SxProps,
  Theme,
  type TextFieldProps,
  Typography,
  Box
} from "@mui/material";

import { RootState } from "../../store";
import { Employee } from "../../interfaces/User";
import { ServiceProvider } from "../../interfaces/ServiceProvidersInterface";
import { resourceCentreEmployeeActions, serviceProviderActions } from "../../actions";

interface PropTypes {
  label?: string;
  disabled?: boolean;
  selectedEmployee?: Employee | ServiceProvider | null;
  onChange: (value: Employee | ServiceProvider | null) => void;
  TextFieldProps?: TextFieldProps;
  sx?: SxProps<Theme>;
}

export default function EmployeeAndSpSelect({
  label = "",
  onChange,
  selectedEmployee,
  disabled = false,
  TextFieldProps = {},
  sx
}: PropTypes): ReactNode {
  const dispatch = useDispatch();

  // selectors
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentreId);
  const rcEmployees = useSelector((state: RootState) =>
    state.resources.resourceCentreEmployees.filter((emp: Employee) => emp.active)
  );
  const rcServiceProviders = useSelector((state: RootState) =>
    state.resources.resourceCentreServiceProviders.filter((sp: ServiceProvider) => sp.active)
  );

  const allEmployees = [...rcEmployees, ...rcServiceProviders];

  // use efffects
  useEffect(() => {
    (() => {
      if (!rcEmployees?.length) {
        dispatch(
          resourceCentreEmployeeActions.getResourceCentreEmployees({ resourceCentreId: rcId })
        );
      }

      if (!rcServiceProviders?.length) {
        dispatch(
          serviceProviderActions.getResourceCentreServiceProviders({
            resourceCentreId: rcId
          })
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcEmployees?.length, rcId, rcServiceProviders?.length]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      sx={sx}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      value={selectedEmployee}
      disabled={disabled}
      options={allEmployees || []}
      getOptionLabel={(option: any) =>
        option.label || `${option?.firstName || ""} ${option?.lastName || ""}`
      }
      isOptionEqualToValue={(option: any, value: any) => {
        const optionAuthenticable = option.userAuthenticable?.[0]?.authenticable;
        const valueAuthenticable = value?.userAuthenticable?.[0]?.authenticable;
        return optionAuthenticable === valueAuthenticable && option.id === value?.id;
      }}
      renderOption={(props, option: any) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <li {...props} key={`${option.userAuthenticable?.[0]?.authenticable}-${option.id}`}>
          <Box>
            <Typography>
              {option?.firstName || ""} {option?.lastName || ""}
            </Typography>
            {option?.phone && <Typography variant="caption">{option.phone}</Typography>}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          //   eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...TextFieldProps}
          variant="outlined"
          fullWidth
          label={label}
          size="small"
          placeholder="Select Employee"
          slotProps={{
            inputLabel: { shrink: true }
          }}
        />
      )}
    />
  );
}
